import type { TableElementValue } from '@vedalib/editor/lib/elements'
import { useDebounceFn } from 'ahooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsActiveElement } from 'services/Store/Project/selectors'

import type { TableContextValue } from './TableContext'
import TableContext from './TableContext'
import * as s from './TableElement.module.scss'
import Table from './components/Table'
import type {
  CellPosition,
  DndControlsCoordinates,
  DndControlsState,
  TableElementType,
} from './tableTypes'

const TableElement: TableElementType = ({
  styles,
  element: { value, id },
  mode,
  waiting,
  font,
  onChange: onChangeProp,
}) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableValue, setTableValue] = useState(value)
  const [cellNodes, setCellNodes] = useState<HTMLTableDataCellElement[][]>([])
  const [dndControlsCoordinates, setDndControlsCoordinates] = useState<DndControlsCoordinates>({
    x: null,
    y: null,
  })
  const [dragState, setDragState] = useState<DndControlsState>({
    dragType: null,
    dragIndex: null,
  })

  const rtProps = useGetRichTextProps({ elementId: id, mode })
  const isActive = useProjectContext(getIsActiveElement, id)

  const [selectedCells, setSelectedCells] = useState<CellPosition[]>([])
  const [startSelectPosition, setStartSelectPosition] = useState<CellPosition | null>(null)

  const { run: onChangeRun, flush } = useDebounceFn(
    (value: TableElementValue) => {
      onChangeProp?.(value)
    },
    { wait: 500 },
  )

  const onChange = useCallback(
    (value: TableElementValue) => {
      setTableValue(value)
      onChangeRun(value)
    },
    [onChangeRun],
  )

  const contextValue: TableContextValue = useMemo(
    () => ({
      cellNodes,
      styles,
      tableRef,
      mode,
      tableValue,
      onChange,
      dndControlsCoordinates,
      setDndControlsCoordinates,
      dragState,
      setDragState,
      selectedCells,
      setSelectedCells,
      startSelectPosition,
      setStartSelectPosition,
      setCellNodes,
      value,
      id,
      rtProps,
      waiting,
      font,
    }),
    [
      cellNodes,
      tableRef,
      styles,
      mode,
      tableValue,
      dndControlsCoordinates,
      setDndControlsCoordinates,
      dragState,
      setDragState,
      selectedCells,
      setSelectedCells,
      startSelectPosition,
      setStartSelectPosition,
      value,
      font,
      id,
      onChange,
      rtProps,
      waiting,
    ],
  )

  useEffect(() => {
    if (!isActive) {
      setTableValue(value)
      setSelectedCells([])
      flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, value])

  return (
    <TableContext.Provider value={contextValue}>
      <div className={s.root}>
        <Table />
      </div>
    </TableContext.Provider>
  )
}

export default TableElement
